import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Briefcase, MapPin, Clock, Calendar, MoreVertical, Eye, Pencil, Trash2, Users, LayoutGrid, List } from 'lucide-react';
import './RecruitmentDashboard.css';

const statusColors = { Open: { bg: '#dcfce7', color: '#166534' }, Closed: { bg: '#fee2e2', color: '#991b1b' }, 'On Hold': { bg: '#fef9c3', color: '#854d0e' } };
const typeColors = { 'Full-time': { bg: '#eff6ff', color: '#1d4ed8' }, 'Part-time': { bg: '#fdf4ff', color: '#7e22ce' }, 'Contract': { bg: '#fff7ed', color: '#c2410c' }, 'Internship': { bg: '#f0fdf4', color: '#15803d' } };

const JobsList = () => {
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [typeFilter, setTypeFilter] = useState('All');
    const [viewMode, setViewMode] = useState('table');
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [viewJob, setViewJob] = useState(null);
    const [currentJobId, setCurrentJobId] = useState(null);

    const initialFormState = {
        title: '', department: '', description: '', requirements: '',
        type: 'Full-time', location: 'On-site', deadline: '', status: 'Open'
    };
    const [formData, setFormData] = useState(initialFormState);

    useEffect(() => { fetchJobs(); fetchCandidates(); }, []);
    useEffect(() => {
        const close = () => setActiveDropdown(null);
        window.addEventListener('click', close);
        return () => window.removeEventListener('click', close);
    }, []);

    const fetchJobs = async () => {
        try {
            const response = await api.get('/recruitment/jobs');
            setJobs(response.data);
        } catch (error) { console.error("Error fetching jobs:", error); }
        finally { setLoading(false); }
    };
    const fetchCandidates = async () => {
        try { const res = await api.get('/recruitment/candidates'); setCandidates(res.data); }
        catch (err) { console.error("Failed to load candidates", err); }
    };

    const getApplicantCount = (jobId) => candidates.filter(c => c.jobId?._id === jobId || c.jobId === jobId).length;

    const handleFormChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleAddClick = () => { setIsEditing(false); setCurrentJobId(null); setFormData(initialFormState); setShowModal(true); };
    const handleEditClick = (job) => {
        setIsEditing(true); setCurrentJobId(job._id);
        setFormData({ title: job.title, department: job.department, description: job.description, requirements: job.requirements || '', type: job.type, location: job.location, deadline: job.deadline ? new Date(job.deadline).toISOString().split('T')[0] : '', status: job.status });
        setShowModal(true); setActiveDropdown(null);
    };
    const handleViewClick = (job) => { setViewJob(job); setActiveDropdown(null); };
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEditing) { await api.put(`/recruitment/jobs/${currentJobId}`, formData); }
            else { await api.post('/recruitment/jobs', formData); }
            setShowModal(false); setFormData(initialFormState); setIsEditing(false); setCurrentJobId(null);
            fetchJobs();
        } catch (error) { console.error('Error saving job', error); alert('Failed to save job'); }
    };
    const toggleDropdown = (id, e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === id ? null : id); };
    const handleDelete = async (id) => {
        if (!window.confirm('Delete this job posting?')) return;
        try { await api.delete(`/recruitment/jobs/${id}`); fetchJobs(); } catch (err) { alert('Failed to delete job'); }
        setActiveDropdown(null);
    };

    const filteredJobs = jobs.filter(job => {
        const matchSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) || job.department.toLowerCase().includes(searchTerm.toLowerCase());
        const matchStatus = statusFilter === 'All' || job.status === statusFilter;
        const matchType = typeFilter === 'All' || job.type === typeFilter;
        return matchSearch && matchStatus && matchType;
    });

    const stats = { total: jobs.length, open: jobs.filter(j => j.status === 'Open').length, closed: jobs.filter(j => j.status === 'Closed').length, onHold: jobs.filter(j => j.status === 'On Hold').length };

    if (loading) return (
        <div className="recruitment-container">
            {/* Header */}
            <div className="recruitment-header">
                <div>
                    <div className="skeleton skeleton-title" style={{ width: 80, marginBottom: 8 }} />
                    <div className="skeleton skeleton-text" style={{ width: 110 }} />
                </div>
                <div className="skeleton skeleton-btn" style={{ width: 140 }} />
            </div>

            {/* 4 Stat Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                {[1,2,3,4].map(i => (
                    <div key={i} style={{ background: 'white', borderRadius: 10, padding: '16px 20px', border: '1px solid #e2e8f0' }}>
                        <div className="skeleton skeleton-text" style={{ width: '60%', marginBottom: 8 }} />
                        <div className="skeleton" style={{ width: '40%', height: 30, borderRadius: 6 }} />
                    </div>
                ))}
            </div>

            {/* Filters bar */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', background: 'white', padding: '14px 18px', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                <div className="skeleton" style={{ flex: 1, height: 36, borderRadius: 8 }} />
                <div className="skeleton" style={{ width: 110, height: 36, borderRadius: 8 }} />
                <div className="skeleton" style={{ width: 100, height: 36, borderRadius: 8 }} />
                <div className="skeleton" style={{ width: 72, height: 36, borderRadius: 8 }} />
            </div>

            {/* Table */}
            <div className="dark-card" style={{ padding: 0, overflow: 'hidden' }}>
                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '11px 14px', borderBottom: '1.5px solid #e8edf2', background: '#f8fafc', gap: 0 }}>
                    {[{ w: 180 }, { w: 110 }, { w: 90 }, { w: 110 }, { w: 70 }, { w: 90 }, { w: 72 }, { w: 50 }].map((col, i) => (
                        <div key={i} className="skeleton" style={{ height: 11, borderRadius: 4, width: col.w, marginRight: i < 7 ? 14 : 0, flexShrink: 0 }} />
                    ))}
                </div>
                {/* Data rows */}
                {[1,2,3,4,5,6].map(i => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '13px 14px', borderBottom: '1px solid #f1f5f9', gap: 0 }}>
                        {/* Job Title + posted date */}
                        <div style={{ width: 180, flexShrink: 0, marginRight: 14 }}>
                            <div className="skeleton skeleton-text" style={{ width: '80%', marginBottom: 5 }} />
                            <div className="skeleton skeleton-text" style={{ width: '55%' }} />
                        </div>
                        {/* Department */}
                        <div className="skeleton skeleton-text" style={{ width: 90, flexShrink: 0, marginRight: 14 }} />
                        {/* Type badge */}
                        <div className="skeleton" style={{ width: 74, height: 22, borderRadius: 20, flexShrink: 0, marginRight: 14 }} />
                        {/* Location */}
                        <div className="skeleton skeleton-text" style={{ width: 90, flexShrink: 0, marginRight: 14 }} />
                        {/* Applicants count */}
                        <div className="skeleton skeleton-text" style={{ width: 30, flexShrink: 0, marginRight: 14 }} />
                        {/* Deadline */}
                        <div className="skeleton skeleton-text" style={{ width: 78, flexShrink: 0, marginRight: 14 }} />
                        {/* Status badge */}
                        <div className="skeleton" style={{ width: 58, height: 22, borderRadius: 20, flexShrink: 0, marginRight: 14 }} />
                        {/* Action icon */}
                        <div className="skeleton" style={{ width: 28, height: 28, borderRadius: 6, flexShrink: 0 }} />
                    </div>
                ))}
                {/* Footer */}
                <div style={{ padding: '14px 20px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
                    <div className="skeleton skeleton-text" style={{ width: 140 }} />
                </div>
            </div>
        </div>
    );

    return (
        <div className="recruitment-container">
            {/* Header */}
            <div className="recruitment-header">
                <div>
                    <h2>Jobs</h2>
                    <div className="breadcrumb"><span className="bc-parent">Recruit</span><span className="bc-sep"> • </span><span className="bc-current">Jobs</span></div>
                </div>
                <button onClick={handleAddClick} className="rc-btn rc-btn-primary">
                    <Plus size={16} /> Post New Job
                </button>
            </div>

            {/* Stats */}
            <div className="rc-stat-grid">
                {[{ label: 'Total Jobs', val: stats.total, color: '#6366f1', bg: '#eef2ff', icon: <Briefcase size={20}/> },
                  { label: 'Open', val: stats.open, color: '#10b981', bg: '#ecfdf5', icon: <Users size={20}/> },
                  { label: 'On Hold', val: stats.onHold, color: '#f59e0b', bg: '#fffbeb', icon: <Clock size={20}/> },
                  { label: 'Closed', val: stats.closed, color: '#ef4444', bg: '#fff1f2', icon: <Calendar size={20}/> }].map((s, i) => (
                    <div key={i} className="rc-stat-card" onClick={() => setStatusFilter(s.label === 'Total Jobs' ? 'All' : s.label)}>
                        <div>
                            <div className="rc-stat-label">{s.label}</div>
                            <div className="rc-stat-value" style={{ color: s.color }}>{s.val}</div>
                        </div>
                        <div className="rc-stat-icon" style={{ background: s.bg, color: s.color }}>{s.icon}</div>
                    </div>
                ))}
            </div>

            {/* Filters & View Toggle */}
            <div className="rc-filters">
                <div className="rc-search-wrap">
                    <Search size={15} />
                    <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search by title or department..." />
                </div>
                <select className="rc-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="All">All Status</option>
                    <option>Open</option><option>Closed</option><option>On Hold</option>
                </select>
                <select className="rc-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                    <option value="All">All Types</option>
                    <option>Full-time</option><option>Part-time</option><option>Contract</option><option>Internship</option>
                </select>
                <div className="rc-view-toggle">
                    <button onClick={() => setViewMode('table')} title="Table view" className={viewMode === 'table' ? 'active' : ''}><List size={16} /></button>
                    <button onClick={() => setViewMode('grid')} title="Grid view" className={viewMode === 'grid' ? 'active' : ''}><LayoutGrid size={16} /></button>
                </div>
            </div>

            {/* Grid View */}
            {viewMode === 'grid' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16, marginBottom: 24 }}>
                    {filteredJobs.length === 0 && <div style={{ gridColumn: '1/-1', textAlign: 'center', color: '#94a3b8', padding: 48 }}>No jobs found</div>}
                    {filteredJobs.map(job => {
                        const sc = statusColors[job.status] || { bg: '#f1f5f9', color: '#475569' };
                        const tc = typeColors[job.type] || { bg: '#f1f5f9', color: '#475569' };
                        const appCount = getApplicantCount(job._id);
                        return (
                            <div key={job._id} style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20, position: 'relative', transition: 'all 0.2s' }}
                                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.08)'}
                                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                                    <div style={{ width: 44, height: 44, borderRadius: 10, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6366f1' }}><Briefcase size={20} /></div>
                                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                        <span style={{ ...sc, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{job.status}</span>
                                        <button onClick={e => toggleDropdown(job._id, e)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '4px', display: 'flex' }}><MoreVertical size={16} /></button>
                                        {activeDropdown === job._id && (
                                            <div style={{ position: 'absolute', right: 16, top: 56, background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 10, minWidth: 140, overflow: 'hidden' }}>
                                                <div style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, color: '#334155', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #f1f5f9' }} onClick={() => handleViewClick(job)}><Eye size={14} /> View</div>
                                                <div style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, color: '#334155', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #f1f5f9' }} onClick={() => handleEditClick(job)}><Pencil size={14} /> Edit</div>
                                                <div style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 8 }} onClick={() => handleDelete(job._id)}><Trash2 size={14} /> Delete</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', marginBottom: 4 }}>{job.title}</div>
                                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>{job.department}</div>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                                    <span style={{ ...tc, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{job.type}</span>
                                    <span style={{ background: '#f8fafc', color: '#64748b', padding: '3px 10px', borderRadius: 20, fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}><MapPin size={10} /> {job.location}</span>
                                </div>
                                <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12, display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#94a3b8' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Users size={12} /> {appCount} applicants</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Calendar size={12} /> {job.deadline ? new Date(job.deadline).toLocaleDateString() : 'No deadline'}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Table View */}
            {viewMode === 'table' && (
                <div className="dark-card" style={{ padding: 0, overflow: 'hidden' }}>
                    <table className="dark-table">
                        <thead>
                            <tr>
                                <th>Job Title</th>
                                <th>Department</th>
                                <th>Type</th>
                                <th>Location</th>
                                <th>Applicants</th>
                                <th>Deadline</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredJobs.length > 0 ? filteredJobs.map(job => {
                                const sc = statusColors[job.status] || { bg: '#f1f5f9', color: '#475569' };
                                const appCount = getApplicantCount(job._id);
                                return (
                                    <tr key={job._id}>
                                        <td>
                                            <div style={{ fontWeight: 600, color: '#1e293b' }}>{job.title}</div>
                                            <div style={{ fontSize: 11, color: '#94a3b8' }}>Posted {new Date(job.createdAt).toLocaleDateString()}</div>
                                        </td>
                                        <td style={{ color: '#475569' }}>{job.department}</td>
                                        <td>
                                            <span style={{ ...(typeColors[job.type] || {}), padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{job.type}</span>
                                        </td>
                                        <td style={{ fontSize: 13, color: '#475569' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><MapPin size={12} style={{ color: '#94a3b8' }} /> {job.location}</div>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#6366f1', fontWeight: 600, fontSize: 13 }} onClick={() => navigate('/recruitment/job-applications')}>
                                                <Users size={14} /> {appCount}
                                            </div>
                                        </td>
                                        <td style={{ fontSize: 13, color: '#475569' }}>{job.deadline ? new Date(job.deadline).toLocaleDateString() : '—'}</td>
                                        <td><span style={{ ...sc, padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{job.status}</span></td>
                                        <td style={{ position: 'relative' }}>
                                            <button onClick={e => toggleDropdown(job._id, e)} className="rc-icon-btn"><MoreVertical size={18} /></button>
                                            {activeDropdown === job._id && (
                                                <div style={{ position: 'absolute', right: 8, top: 36, background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 50, minWidth: 140, overflow: 'hidden' }}>
                                                    <div style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, color: '#334155', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #f1f5f9' }} onClick={() => handleViewClick(job)}><Eye size={14} /> View</div>
                                                    <div style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, color: '#334155', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #f1f5f9' }} onClick={() => handleEditClick(job)}><Pencil size={14} /> Edit</div>
                                                    <div style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 8 }} onClick={() => handleDelete(job._id)}><Trash2 size={14} /> Delete</div>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            }) : (
                                <tr><td colSpan="8" style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>No jobs found. Click "Post New Job" to create one.</td></tr>
                            )}
                        </tbody>
                    </table>
                    <div className="rc-table-footer">
                        Showing {filteredJobs.length} of {jobs.length} jobs
                    </div>
                </div>
            )}

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="rc-modal-overlay">
                    <div className="rc-modal wide">
                        <div className="rc-modal-header">
                            <h3 className="rc-modal-title">{isEditing ? 'Edit Job' : 'Post New Job'}</h3>
                            <button className="rc-modal-close" onClick={() => setShowModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleFormSubmit}>
                            <div className="form-group">
                                <label>Job Title *</label>
                                <input name="title" required value={formData.title} onChange={handleFormChange} placeholder="e.g. Senior Software Engineer" />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                <div className="form-group">
                                    <label>Department *</label>
                                    <input name="department" required value={formData.department} onChange={handleFormChange} placeholder="e.g. Engineering" />
                                </div>
                                <div className="form-group">
                                    <label>Job Type</label>
                                    <select name="type" value={formData.type} onChange={handleFormChange}>
                                        <option>Full-time</option><option>Part-time</option><option>Contract</option><option>Internship</option>
                                    </select>
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                <div className="form-group">
                                    <label>Location</label>
                                    <input name="location" value={formData.location} onChange={handleFormChange} placeholder="e.g. Remote / Pune" />
                                </div>
                                <div className="form-group">
                                    <label>Application Deadline</label>
                                    <input type="date" name="deadline" value={formData.deadline} onChange={handleFormChange} />
                                </div>
                            </div>
                            {isEditing && (
                                <div className="form-group">
                                    <label>Status</label>
                                    <select name="status" value={formData.status} onChange={handleFormChange}>
                                        <option>Open</option><option>On Hold</option><option>Closed</option>
                                    </select>
                                </div>
                            )}
                            <div className="form-group">
                                <label>Job Description *</label>
                                <textarea name="description" required rows={4} value={formData.description} onChange={handleFormChange} placeholder="Describe the role, responsibilities..." />
                            </div>
                            <div className="form-group">
                                <label>Requirements</label>
                                <textarea name="requirements" rows={3} value={formData.requirements} onChange={handleFormChange} placeholder="Skills, qualifications, experience..." />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
                                <button type="button" onClick={() => setShowModal(false)} className="rc-btn rc-btn-secondary">Cancel</button>
                                <button type="submit" className="rc-btn rc-btn-primary">{isEditing ? 'Update Job' : 'Post Job'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* View Details Modal */}
            {viewJob && (
                <div className="rc-modal-overlay">
                    <div className="rc-modal">
                        <div className="rc-modal-header">
                            <div>
                                <h3 className="rc-modal-title">{viewJob.title}</h3>
                                <div style={{ fontSize: 12, color: 'var(--rc-text-2)', marginTop: 2 }}>{viewJob.department}</div>
                            </div>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                <span className="rc-badge" style={{ ...(statusColors[viewJob.status] || {}) }}>{viewJob.status}</span>
                                <button className="rc-modal-close" onClick={() => setViewJob(null)}>×</button>
                            </div>
                        </div>
                        <div style={{ padding: 24 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                                {[{ l: 'Type', v: viewJob.type }, { l: 'Location', v: viewJob.location }, { l: 'Posted', v: new Date(viewJob.createdAt).toLocaleDateString() }, { l: 'Deadline', v: viewJob.deadline ? new Date(viewJob.deadline).toLocaleDateString() : 'No deadline' }].map((f, i) => (
                                    <div key={i} style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px' }}>
                                        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>{f.l}</div>
                                        <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 500 }}>{f.v}</div>
                                    </div>
                                ))}
                            </div>
                            <div style={{ marginBottom: 16 }}>
                                <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 8, fontSize: 13 }}>Description</div>
                                <div style={{ color: '#475569', fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap', background: '#f8fafc', borderRadius: 8, padding: 14 }}>{viewJob.description}</div>
                            </div>
                            {viewJob.requirements && (
                                <div style={{ marginBottom: 16 }}>
                                    <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 8, fontSize: 13 }}>Requirements</div>
                                    <div style={{ color: '#475569', fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap', background: '#f8fafc', borderRadius: 8, padding: 14 }}>{viewJob.requirements}</div>
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 16, borderTop: '1px solid var(--rc-border-light)' }}>
                                <button onClick={() => { setViewJob(null); handleEditClick(viewJob); }} className="rc-btn rc-btn-primary"><Pencil size={13} /> Edit Job</button>
                                <button onClick={() => setViewJob(null)} className="rc-btn rc-btn-secondary">Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JobsList;
